/**
 * components/ProtectedRoute.tsx
 * ──────────────────────────────
 * Higher-order component that enforces RBAC before rendering a route.
 *
 * Usage
 * ─────
 *   <Route
 *     path="/dashboard/inventory"
 *     element={
 *       <ProtectedRoute allowedRoles={['customer', 'admin']}>
 *         <CustomerDashboard />
 *       </ProtectedRoute>
 *     }
 *   />
 *
 * Behaviour
 * ─────────
 * • isLoading  → renders a full-screen spinner (avoids flash-of-redirect)
 * • !user      → redirects to /  (login page)
 * • wrong role → redirects to /unauthorized
 * • correct    → renders children
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@/types';
import { Spinner } from '@/components/ui/Spinner';

interface ProtectedRouteProps {
  allowedRoles: UserRole[];
  children: React.ReactNode;
}

export function ProtectedRoute({ allowedRoles, children }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="pr-loading">
        <Spinner size={48} label="Verifying credentials…" />
      </div>
    );
  }

  if (!user) {
    // Preserve attempted path so we can redirect back after login if desired
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
